import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Plus } from 'lucide-react';
import Icon from '../../components/AppIcon';
import ChemicalInventory from './components/ChemicalInventory';
import ChemicalProfile from './components/ChemicalProfile';
import ReactivityMatrix from './components/ReactivityMatrix';
import SearchFilters from './components/SearchFilters';
import ExportPanel from './components/ExportPanel';

const ChemicalInformationManager = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChemical, setSelectedChemical] = useState(null);
  const [activeFilters, setActiveFilters] = useState({
    hazardClass: [],
    physicalState: [],
    regulatoryStatus: []
  });

  const chemicalInventory = [
    {
      id: 'chem-1',
      name: 'Acetone',
      casNumber: '67-64-1',
      formula: 'C3H6O',
      hazardClass: ['Flammable', 'Irritant'],
      physicalState: 'Liquid',
      flashPoint: '-20°C',
      autoignitionTemp: '465°C',
      flammabilityLimits: { lower: '2.5%', upper: '12.8%' },
      regulatoryStatus: 'Regulated',
      msdsVersion: 'Rev 4.0',
      lastUpdated: '12/01/2025',
      exposure: {
        twa: '500 ppm',
        stel: '750 ppm',
        ceiling: '1000 ppm'
      }
    },
    {
      id: 'chem-2',
      name: 'Benzene',
      casNumber: '71-43-2',
      formula: 'C6H6',
      hazardClass: ['Carcinogen', 'Flammable'],
      physicalState: 'Liquid',
      flashPoint: '-11°C',
      autoignitionTemp: '498°C',
      flammabilityLimits: { lower: '1.2%', upper: '7.8%' },
      regulatoryStatus: 'Highly Regulated',
      msdsVersion: 'Rev 3.2',
      lastUpdated: '11/28/2025',
      exposure: {
        twa: '0.5 ppm',
        stel: '2.5 ppm',
        ceiling: '5 ppm'
      }
    },
    {
      id: 'chem-3',
      name: 'Methanol',
      casNumber: '67-56-1',
      formula: 'CH3OH',
      hazardClass: ['Toxic', 'Flammable'],
      physicalState: 'Liquid',
      flashPoint: '11°C',
      autoignitionTemp: '464°C',
      flammabilityLimits: { lower: '6.0%', upper: '36.0%' },
      regulatoryStatus: 'Regulated',
      msdsVersion: 'Rev 2.8',
      lastUpdated: '12/03/2025',
      exposure: {
        twa: '200 ppm',
        stel: '250 ppm',
        ceiling: '300 ppm'
      }
    }
  ];

  const inventoryMetrics = {
    totalChemicals: 248,
    highHazard: 43,
    regulatedSubstances: 156,
    pendingMSDSUpdates: 12
  };

  const handleChemicalSelect = (chemical) => {
    setSelectedChemical(chemical);
  };

  const handleBackToInventory = () => {
    setSelectedChemical(null);
  };

  const handleExport = (format) => {
    console.log('Exporting in format:', format);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Chemical Information Manager</h1>
              <p className="text-sm text-gray-600 mt-1">Comprehensive chemical inventory and safety data management</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Icon name="Home" size={16} />
                Return to Dashboard
              </button>
              <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">
                <Icon name="Plus" size={16} />
                Add New Chemical
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 lg:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Inventory or Filters */}
          <div className="lg:col-span-1 space-y-6">
            <SearchFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              activeFilters={activeFilters}
              onFilterChange={setActiveFilters}
            />
            <ExportPanel onExport={handleExport} />
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            {selectedChemical ? (
              <ChemicalProfile
                chemical={selectedChemical}
                onClose={handleBackToInventory}
              />
            ) : (
              <ChemicalInventory
                chemicals={chemicalInventory}
                searchQuery={searchQuery}
                activeFilters={activeFilters}
                onChemicalSelect={handleChemicalSelect}
              />
            )}
          </div>
        </div>

        {/* Reactivity Matrix Section */}
        {!selectedChemical && (
          <div className="mt-8">
            <ReactivityMatrix chemicals={chemicalInventory} />
          </div>
        )}

        {/* Help Section */}
        <div className="mt-8 bg-card border border-border rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
              <Icon name="Info" size={24} color="var(--color-accent)" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Chemical Safety Management</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Maintain comprehensive chemical documentation including MSDS/SDS, reactivity data, and hazard assessments. 
                Use the reactivity matrix to identify incompatible chemicals and ensure safe storage and handling.
              </p>
              <div className="flex flex-wrap gap-3">
                <button className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium">
                  <Icon name="BookOpen" size={16} />
                  Safety Guidelines
                </button>
                <button className="inline-flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors text-sm font-medium">
                  <Icon name="Download" size={16} />
                  Export Inventory
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChemicalInformationManager;